/**
 * useBackForwardNavigation
 *
 * Detects browser Back/Forward navigation and invokes a callback so the page
 * can re-hydrate from sessionStorage. Covers two cases:
 *
 * 1. **Fresh load via Back/Forward** — `PerformanceNavigationTiming.type` is
 *    `'back_forward'`. Use `isBackForwardNavigation()` synchronously inside
 *    `onMounted` to decide the initial source of truth.
 * 2. **BFCache restore** (Safari/Firefox) — the page is restored from memory,
 *    Vue's `onMounted` does NOT fire. The `pageshow` event with
 *    `event.persisted === true` is the only signal.
 *
 * `popstate` is intentionally not listened to: VueApp is multi-page and uses
 * `replaceState` only, so a back navigation triggers either a full reload
 * (handled by `onMounted` + `isBackForwardNavigation()`) or a BFCache restore
 * (handled by `pageshow.persisted`). A `popstate` listener would be redundant
 * with the BFCache path and would risk double-firing the callback.
 */

import { onMounted, onUnmounted } from 'vue';

interface BackForwardNavigationOptions {
  onBackForward: () => void | Promise<void>;
}

/**
 * Returns true if the current page load is the result of a browser back/forward.
 * Pure function — safe to call any time after `performance` is available.
 */
export function isBackForwardNavigation(): boolean {
  try {
    const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    return entries[0]?.type === 'back_forward';
  } catch {
    return false;
  }
}

export function useBackForwardNavigation({ onBackForward }: BackForwardNavigationOptions): void {
  function handlePageShow(event: PageTransitionEvent): void {
    if (!event.persisted) return;
    void onBackForward();
  }

  onMounted(() => {
    window.addEventListener('pageshow', handlePageShow);
  });

  onUnmounted(() => {
    window.removeEventListener('pageshow', handlePageShow);
  });
}
